import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { AdminConfig } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { useAuth } from '../Auth.js';
import { LogoCropper } from '../components/LogoCropper.js';
import { useToast } from '../components/ui.js';

/**
 * Area administrativa: login simples (secao 8 do CLAUDE.md — nada de auth
 * complexa na fase 1) para guardar a chave da OpenAI sem precisar de acesso ao
 * servidor. A chave nunca volta inteira do backend: a tela so mostra um trecho
 * para o usuario reconhecer o que esta gravado.
 */
export function AdminPage() {
  const toast = useToast();
  const { refresh } = useAppData();
  const { session, refreshSession, logout } = useAuth();

  return (
    <main className="page">
      <div className="page-head">
        <h1>Administracao</h1>
        <div className="spacer" />
        <span className="small muted">{session.username}</span>
        <button
          type="button"
          className="btn"
          onClick={() => {
            void logout().then(() => toast('Sessao encerrada.', 'ok'));
          }}
        >
          Sair
        </button>
      </div>

      {session.usingDefaultPassword ? (
        <div className="card card-warn">
          <strong>Troque a senha.</strong> Esta instalacao ainda usa a senha de fabrica, e o
          endereco esta publico na internet — hoje a senha padrao e a unica coisa entre a internet
          e o estoque da sua casa.
        </div>
      ) : null}

      <LogoCard />
      <OpenAiCard onSaved={() => void refresh()} />
      <PasswordCard onChanged={() => void refreshSession()} />
    </main>
  );
}

/** Envio e recorte do logo do sistema (barra, tela de login e icone da aba). */
function LogoCard() {
  const toast = useToast();
  const { branding, logoUrl, refreshBranding } = useAuth();
  const [trocando, setTrocando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  async function salvar(dataUrl: string) {
    setSalvando(true);
    try {
      await api.saveLogo(dataUrl);
      await refreshBranding();
      setTrocando(false);
      toast('Logo atualizado.', 'ok');
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!window.confirm('Remover o logo? O sistema volta a desenhar o logotipo padrao.')) return;
    try {
      await api.removeLogo();
      await refreshBranding();
      toast('Logo removido.', 'ok');
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }

  return (
    <section className="card">
      <h2>Logo do sistema</h2>
      <p className="small muted">
        Aparece na barra do topo, na tela de login e como icone da aba. O recorte e largo (3:1) —
        arraste e aproxime para tirar a sobra de margem e centralizar o logotipo.
      </p>

      {logoUrl && !trocando ? (
        <>
          <div className="logo-atual">
            <img src={logoUrl} alt="Logo atual" />
          </div>
          <div className="row">
            <button type="button" className="btn btn-primary" onClick={() => setTrocando(true)}>
              Trocar logo
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void remover()}>
              Remover
            </button>
          </div>
        </>
      ) : (
        <LogoCropper salvando={salvando} onSave={salvar} onCancel={() => setTrocando(false)} />
      )}

      {!branding.hasLogo && !trocando ? (
        <p className="small muted">Nenhum logo enviado — o Almox desenha o logotipo padrao.</p>
      ) : null}
    </section>
  );
}

function OpenAiCard({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [key, setKey] = useState('');
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const current = await api.adminConfig();
      setConfig(current);
      setModel(current.visionModel);
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      // Campo em branco nao apaga a chave gravada — para isso ha o botao Remover.
      const next = await api.saveAdminConfig({
        ...(key.trim() ? { openaiApiKey: key.trim() } : {}),
        visionModel: model.trim() || null,
      });
      setConfig(next);
      setKey('');
      toast('Configuracao salva.', 'ok');
      onSaved();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Remover a chave gravada? A leitura por foto deixa de funcionar.')) return;
    setBusy(true);
    try {
      setConfig(await api.saveAdminConfig({ openaiApiKey: '' }));
      setKey('');
      toast('Chave removida.', 'ok');
      onSaved();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      const result = await api.testOpenAiKey(key.trim() || undefined);
      toast(result.message, result.ok ? 'ok' : 'error');
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>Chave da OpenAI</h2>
      <p className="small muted">
        Usada apenas para identificar produto novo pela foto da embalagem. Fica gravada no servidor
        e nunca volta inteira para o navegador. Sem ela, o resto do Almox funciona igual.
      </p>

      <p className="small">
        {config?.openaiKeyConfigured ? (
          <>
            <span className="badge badge-ok">configurada</span> <code>{config.openaiKeyHint}</code>{' '}
            <span className="muted">
              ({config.openaiKeySource === 'database' ? 'gravada aqui' : 'vinda do ambiente'})
            </span>
          </>
        ) : (
          <span className="badge badge-danger">nenhuma chave configurada</span>
        )}
      </p>

      <form onSubmit={save}>
        <div className="field">
          <label htmlFor="openai-key">
            {config?.openaiKeyConfigured ? 'Substituir pela chave' : 'Chave'}
          </label>
          <input
            id="openai-key"
            type="password"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="sk-..."
            autoComplete="off"
            spellCheck={false}
          />
          <span className="small muted">
            Em branco, mantem a chave atual. Cole a chave de <code>platform.openai.com</code>.
          </span>
        </div>

        <div className="field">
          <label htmlFor="openai-model">Modelo de visao</label>
          <input
            id="openai-model"
            value={model}
            onChange={(event) => setModel(event.target.value)}
            placeholder="gpt-4o-mini"
            spellCheck={false}
          />
          <span className="small muted">
            <code>gpt-4o-mini</code> da conta de ler embalagem e custa uma fracao do{' '}
            <code>gpt-4o</code>.
          </span>
        </div>

        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="btn" onClick={() => void test()} disabled={busy}>
            Testar chave
          </button>
          {config?.openaiKeySource === 'database' ? (
            <button type="button" className="btn btn-danger" onClick={() => void remove()} disabled={busy}>
              Remover
            </button>
          ) : null}
        </div>
      </form>
    </section>
  );
}

function PasswordCard({ onChanged }: { onChanged: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      toast('A confirmacao nao confere com a nova senha.', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.changeAdminPassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      toast('Senha alterada.', 'ok');
      onChanged();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>Senha de acesso</h2>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="pass-current">Senha atual</label>
          <input
            id="pass-current"
            type="password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="pass-new">Nova senha</label>
            <input
              id="pass-new"
              type="password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="pass-confirm">Repita a nova senha</label>
            <input
              id="pass-confirm"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Salvando…' : 'Trocar senha'}
        </button>
      </form>
    </section>
  );
}
